import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/pool-definitions',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/benefit-programs-pool-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/pool-definitions',
        destPath: '/v1/pool-definitions',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/pool-definitions',
        destPath: '/v1/bulk/pool-definitions',
      },
    ],
    '*.pub.wix-code.com': [
      {
        srcPath: '/_api/benefit-programs-pool-definitions',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/benefit-programs-pool-definitions',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-pool-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/pool-definitions',
        destPath: '/v1/pool-definitions',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/pool-definitions',
        destPath: '/v1/bulk/pool-definitions',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-pool-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/pool-definitions',
        destPath: '/v1/pool-definitions',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/pool-definitions',
        destPath: '/v1/bulk/pool-definitions',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/benefit-programs-pool-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/pool-definitions',
        destPath: '/v1/pool-definitions',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/pool-definitions',
        destPath: '/v1/bulk/pool-definitions',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/benefit-programs-pool-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/pool-definitions',
        destPath: '/v1/pool-definitions',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/pool-definitions',
        destPath: '/v1/bulk/pool-definitions',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-pool-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/pool-definitions',
        destPath: '/v1/pool-definitions',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/pool-definitions',
        destPath: '/v1/bulk/pool-definitions',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-pool-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/pool-definitions',
        destPath: '/v1/pool-definitions',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/pool-definitions',
        destPath: '/v1/bulk/pool-definitions',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/benefit-programs/v1/pool-definitions',
        destPath: '/v1/pool-definitions',
      },
      {
        srcPath: '/benefit-programs/v1/pool-definitions',
        destPath: '/v1/pool-definitions',
      },
      {
        srcPath: '/benefit-programs/v1/bulk/pool-definitions',
        destPath: '/v1/bulk/pool-definitions',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_benefit-programs_pool-definitions';

/** Creates a pool definition. */
export function createPoolDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __createPoolDefinition({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'poolDefinition.createdDate' },
          { path: 'poolDefinition.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.CreatePoolDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath: '/v1/pool-definitions',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'poolDefinition.createdDate' },
              { path: 'poolDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createPoolDefinition;
}

/** Creates pool definitions. */
export function bulkCreatePoolDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreatePoolDefinitions({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'poolDefinitions.createdDate' },
          { path: 'poolDefinitions.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.BulkCreatePoolDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath: '/v1/bulk/pool-definitions/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreatePoolDefinitions;
}

/**
 * Updates a pool definition.
 *
 * Each time the pool definition is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the pool definition.
 * This ensures you're working with the latest pool definition
 * and prevents unintended overwrites.
 */
export function updatePoolDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __updatePoolDefinition({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'poolDefinition.createdDate' },
          { path: 'poolDefinition.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'PATCH' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.UpdatePoolDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath: '/v1/pool-definitions/{poolDefinition.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'poolDefinition.createdDate' },
              { path: 'poolDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updatePoolDefinition;
}

/** Updates pool definitions. */
export function bulkUpdatePoolDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdatePoolDefinitions({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'poolDefinitions.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'poolDefinitions.poolDefinition.createdDate' },
          { path: 'poolDefinitions.poolDefinition.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.BulkUpdatePoolDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath: '/v1/bulk/pool-definitions/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdatePoolDefinitions;
}

/** Deletes a pool definition. */
export function deletePoolDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __deletePoolDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'DELETE' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.DeletePoolDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath: '/v1/pool-definitions/{poolDefinitionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deletePoolDefinition;
}

/** Deletes pool definitions. */
export function bulkDeletePoolDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeletePoolDefinitions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.BulkDeletePoolDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath: '/v1/bulk/pool-definitions/delete',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkDeletePoolDefinitions;
}

/** Retrieves a pool definition. */
export function getPoolDefinition(payload: object): RequestOptionsFactory<any> {
  function __getPoolDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'GET' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.GetPoolDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath: '/v1/pool-definitions/{poolDefinitionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'poolDefinition.createdDate' },
              { path: 'poolDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPoolDefinition;
}

/**
 * Creates a query to retrieve a list of pool definitions.
 *
 * The Query Pool Definitions method builds a query to retrieve a list of pool definitions and returns a `PoolDefinitionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `PoolDefinitionsQueryBuilder` methods onto the query. `PoolDefinitionsQueryBuilder` methods enable you to filter, sort, and control the results that Query Pool Definitions returns.
 *
 * Query Pool Definitions has a default paging limit of 50, which you can override.
 *
 *  For a full description of the item object, see the object returned for the `items` property in `PoolDefinitionsQueryResult`.
 */
export function queryPoolDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryPoolDefinitions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.QueryPoolDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath: '/v1/pool-definitions/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'poolDefinitions.createdDate' },
              { path: 'poolDefinitions.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryPoolDefinitions;
}

/**
 * Adds a pool definition to a program definition.
 *
 * Pool definitions can be contained by multiple program definitions, so this doesn't affect other program definitions that contain this pool definition.
 */
export function addPoolDefinitionToProgramDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __addPoolDefinitionToProgramDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.AddPoolDefinitionToProgramDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath: '/v1/pool-definitions/program-definition/assign',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'poolDefinition.createdDate' },
              { path: 'poolDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addPoolDefinitionToProgramDefinition;
}

/**
 * Removes a pool definition from a program definition.
 *
 * Pool definitions don't need to be contained by a program definition, so this doesn't affect the usability of this pool definition.
 */
export function removePoolDefinitionFromProgramDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __removePoolDefinitionFromProgramDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.RemovePoolDefinitionFromProgramDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath: '/v1/pool-definitions/program-definition/remove',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'poolDefinition.createdDate' },
              { path: 'poolDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removePoolDefinitionFromProgramDefinition;
}

/** Retrieve all a program definition's pool definitions. */
export function findPoolDefinitionsByProgramDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __findPoolDefinitionsByProgramDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.pool_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.pool_definition.PoolDefinitionService.FindPoolDefinitionsByProgramDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1PoolDefinitionPoolDefinitionServiceUrl({
        protoPath:
          '/v1/pool-definitions/find-pool-definitions-by-program-definition',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'poolDefinitions.createdDate' },
              { path: 'poolDefinitions.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __findPoolDefinitionsByProgramDefinition;
}
